import { FCX, ID } from '@models';
import React, { ReactNode, useMemo } from 'react';
import { iTableConfigItem } from '@components/Table/TableConfig';
import { Tooltip } from 'react-tooltip';
import { getFormattedThousand } from '@core/utils/number';
import { TableContext } from '@components/Table/TableContext';
import { iSeasonalFood } from '@api/useSeasonalByFoods';
import { iCategoryActivityFood } from '@api/useCategoyActivityByFoods';

const FoodFlavorsTableConfig: FCX<{
    id: ID;
    data: iCategoryActivityFood[];
    renderCategoryName: ReactNode;
}> = ({
    id,
    data,
    renderCategoryName,
    children,
}) => {
    const tableConfig = useMemo((): iTableConfigItem[] => [
        {
            key: 'name',
            title: '',
            Value: ({ index }) => <>
                {data[index].name}
            </>,
        },
        {
            key: 'count',
            sortingColName: 'count',
            title: 'Count',
            Value: ({ index }) => <>
                {getFormattedThousand(data[index].count)}
            </>,
            styles: {
                cell: {
                    flex: '0 0 20%',
                    justifyContent: 'center',
                },
            },
        },
        {
            key: 'percentage',
            sortingColName: 'percentage',
            title: <>
                Percentage
                <Tooltip id={`table-category-activity-foods-${id}-percentage`}>
                    <div style={{ textAlign: 'center', width: 100 }}>
                        % of total{' '}
                        {renderCategoryName}
                        {' '}menu launches
                    </div>
                </Tooltip>
            </>,
            Value: ({ index }) => <>
                {Math.round(data[index].percentage || 0)}%
            </>,
            getAttrs: (index) => (index === -1 ? {
                'data-tooltip-id': `table-category-activity-foods-${id}-percentage`,
            } : {}),
            styles: {
                cell: {
                    flex: '0 0 20%',
                    justifyContent: 'center',
                },
                head: {
                    cursor: 'pointer',
                }
            },
        },
        {
            key: 'index',
            sortingColName: 'index',
            title: <>
                Index
                <Tooltip id={`table-category-activity-foods-${id}-index`}>
                    <div style={{ textAlign: 'center', width: 180 }}>
                        100 = average for all seasons;<br/>
                        A value of {'>'}100 indicates<br/>
                        this flavor / ingredient is more prevalent in&nbsp;
                        {renderCategoryName}
                        &nbsp;launches
                    </div>
                </Tooltip>
            </>,
            Value: ({ index }) => {
                const value = data[index].index;
                return (
                    <>
                        {value === Infinity ? '*' : getFormattedThousand(value)}
                    </>
                );
            },
            getAttrs: (index) => (index === -1 ? {
                'data-tooltip-id': `table-category-activity-foods-${id}-index`,
            } : {}),
            styles: {
                cell: {
                    flex: '0 0 20%',
                    justifyContent: 'center',
                },
            },
        },
    ], [data, renderCategoryName, id]);

    return (
        <TableContext.Provider value={{ config: tableConfig }}>
            {children}
        </TableContext.Provider>
    );
};

export default FoodFlavorsTableConfig;